import { useEffect, useState } from "react";
import { IconClose } from "@veto/ui";

const DISMISS_KEY = "veto.install.dismissed";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // All iOS browsers (Safari + CriOS/FxiOS/EdgiOS) install via Share → Add to Home Screen.
  // The old check excluded Chrome/Firefox/Edge on iOS and left those users with no guidance.
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const ios = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(mq || ios);
}

/**
 * Install / Add-to-Home-Screen guidance (NFR-PWA-001 UX).
 * Captures `beforeinstallprompt` on Chromium; shows iOS Share instructions otherwise.
 */
export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState(false);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isStandalone() || hidden) return;

    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setShowIos(false);
      try {
        localStorage.setItem(DISMISS_KEY, "1");
      } catch {
        /* private mode */
      }
      setHidden(true);
    };
    // Standalone can change without reload (e.g. installed in another tab / iPad multitasking).
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onDisplayChange = () => {
      if (isStandalone()) onInstalled();
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    mq?.addEventListener?.("change", onDisplayChange);

    // iOS/iPadOS: no beforeinstallprompt exists — every browser uses the Share sheet.
    // Samsung Internet / Firefox Android also lack BIP; fall back to generic guidance
    // instead of silence so those users still learn how to install.
    if (isIosDevice() && !isStandalone()) {
      setShowIos(true);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onDisplayChange);
    };
  }, [hidden]);

  if (hidden || isStandalone()) return null;
  if (!deferred && !showIos) return null;

  const dismiss = () => {
    setHidden(true);
    setDeferred(null);
    setShowIos(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* private mode */
    }
  };

  const isIos = isIosDevice();
  return (
    <div className="pwa-install" role="status" aria-live="polite">
      <span>
        {deferred
          ? "Install Veto for offline shell, home-screen launch, and notifications."
          : isIos
            ? "On iPhone/iPad: Share → Add to Home Screen to install Veto (required for Home Screen web push on iOS 16.4+)."
            : "Install Veto: browser menu → Install / Add to Home Screen for offline shell and notifications."}
      </span>
      {deferred && (
        <button
          type="button"
          onClick={() => {
            void (async () => {
              await deferred.prompt();
              await deferred.userChoice;
              dismiss();
            })();
          }}
        >
          Install
        </button>
      )}
      <button
        type="button"
        className="pwa-close"
        aria-label="Dismiss install tip"
        title="Dismiss"
        onClick={dismiss}
      >
        <IconClose />
      </button>
    </div>
  );
}
